import { Button, Divider, Form, Icon, Input, notification, PageHeader, Upload } from 'antd';
import React, { useState } from 'react';
import { useAddTemplateMutation, useUpdateTemplateMutation } from '../service';
import { AddTemplateParams, UpdateTemplateParams } from '../types';
import { useNavigate } from 'react-router-dom';

function validateLogoFile(logo: File) {
    let errorMessages: string[] = [];
    const allowedTypes = ['png', 'gif', 'jpg', 'jpeg'];

    // file.type has the same format as a MIME type: type/subtype
    const type = logo.type.split('/')[1];
    if (!allowedTypes.includes(type)) {
        errorMessages = [...errorMessages, 'This file type is not accepted'];
    }
    if (logo.size > 1024 * 1024) {
        errorMessages = [...errorMessages, 'Logo must be smaller than 1Mb!'];
    }
    return errorMessages;
}

function isUrl(string: string) {
    if (string) {
        return string.split(':')[0] === 'https';
    }
    return false;
}

const { TextArea } = Input;
const { Dragger } = Upload;

type Props = {
    templateGuid: string;
    nameValue: string;
    headerValue: string;
    footerValue: string;
    logoValue: string;
    onNameChange: (newName: string) => void;
    onHeaderChange: (newHeader: string) => void;
    onFooterChange: (newFooter: string) => void;
    onLogoChange: (newLogo: string) => void;
};

export default function CustomTemplateForm({
    templateGuid,
    nameValue,
    headerValue,
    footerValue,
    logoValue,
    onNameChange,
    onHeaderChange,
    onFooterChange,
    onLogoChange,
}: Props) {
    const navigate = useNavigate();
    const [nameInputError, setNameInputError] = useState<string>('');
    const [logoErrors, setLogoErrors] = useState<string[]>([]);

    const [logoWasChanged, setLogoWasChanged] = useState<boolean>(false);

    const [addTemplate, { isLoading: isAddingTemplate }] = useAddTemplateMutation();
    const [updateTemplate, { isLoading: isUpdatingTemplate }] = useUpdateTemplateMutation();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!nameValue) {
            setNameInputError('This field is required');
            return;
        }

        if (logoErrors.length > 0) {
            return;
        }

        let payload: Record<string, any> = {
            engine_version: '1.0.0',
            theme: 'default',
            name: nameValue,
            content_blocks: {
                header: headerValue,
                footer: footerValue,
            },
        };

        try {
            if (templateGuid === 'new') {
                payload = {
                    ...payload,
                    base64_logo: logoValue.replace(/^data:.+;base64,/, ''),
                };

                const addedTemplate = await addTemplate(payload as AddTemplateParams).unwrap();
                navigate(`/white-label/templates/${addedTemplate.guid}`);
            } else {
                payload = {
                    ...payload,
                    guid: templateGuid,
                    base64_logo: logoWasChanged ? logoValue.replace(/^data:.+;base64,/, '') : undefined,
                };

                await updateTemplate(payload as UpdateTemplateParams).unwrap();
            }

            notification.success({ message: 'Successfully saved your template' });
        } catch (error) {
            notification.error({ message: 'Something went wrong' });
        }
    };

    // uploaded image is resized and its aspect ratio is maintained
    const beforeUpload = (file: File) =>
        new Promise<void>((resolve, reject) => {
            setLogoErrors([]);

            const logoValidationErrors = validateLogoFile(file);

            if (logoValidationErrors.length > 0) {
                setLogoErrors(logoValidationErrors);
                reject();
                return;
            }

            const height = 70;
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = (event) => {
                const img = new Image();
                img.src = reader.result as string;

                img.onload = () => {
                    const elem = document.createElement('canvas');
                    const scaleFactor = height / img.height;

                    elem.width = img.width * scaleFactor;
                    elem.height = height;
                    const ctx = elem.getContext('2d');

                    if (!ctx) {
                        reject();
                        return;
                    }

                    // img.width and img.height will contain the original dimensions
                    ctx.drawImage(img, 0, 0, img.width * scaleFactor, height);
                    const data = ctx.canvas.toDataURL();
                    // const b64 = data.replace(/^data:.+;base64,/, "");

                    setLogoWasChanged(true);
                    onLogoChange(data);
                    resolve();
                };
            };
        });

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onNameChange(event.target.value);
        setNameInputError('');
    };

    const isLoading = isAddingTemplate || isUpdatingTemplate;

    return (
        <div className="branded-pdf-template">
            <PageHeader
                onBack={() => navigate('/white-label/templates')}
                title="PDF Brand Template"
            />
            <div className="form-description">Apply your brand basic template for all future Branded PDFs</div>
            <Divider />
            <Form
                className="template-form"
                onSubmit={handleSubmit}
            >
                <Form.Item
                    validateStatus={nameInputError ? 'error' : 'validating'}
                    help={nameInputError ? nameInputError : ''}
                >
                    <div className="form-label">Template name</div>
                    <Input
                        placeholder="Template name goes here"
                        name="templateName"
                        onChange={handleNameChange}
                        value={nameValue}
                    />
                </Form.Item>
                <Form.Item
                    validateStatus={logoErrors.length > 0 ? 'error' : 'success'}
                    help={
                        logoErrors.length > 0 ? (
                            <React.Fragment>
                                <div>{logoErrors[0]}</div>
                                <div>{logoErrors[1]}</div>
                            </React.Fragment>
                        ) : (
                            ''
                        )
                    }
                >
                    <div className="form-label flexbox align-center">
                        <div className="step-counter flexbox align-center justify-center">1</div>
                        Company logo
                    </div>
                    <div className="form-description">Allowed types (max 1Mb): png, gif, jpg or jpeg</div>
                    <div></div>
                    <Dragger
                        name="file"
                        customRequest={() => { }}
                        beforeUpload={beforeUpload}
                        multiple={false}
                        showUploadList={false}
                    >
                        <div className="ant-upload-text flexbox align-center justify-center flex-wrap">
                            {logoValue ? (
                                <img
                                    src={isUrl(logoValue) ? `${logoValue}?${new Date().getTime()}` : logoValue}
                                    alt="logo-preview"
                                    crossOrigin="anonymous"
                                />
                            ) : (
                                <Icon type="picture" />
                            )}
                            <div>Drag and drop or select your logo here</div>
                        </div>
                    </Dragger>
                </Form.Item>
                <Divider />
                <Form.Item>
                    <div className="form-label flexbox align-center">
                        <div className="step-counter flexbox align-center justify-center">2</div>
                        Report header
                    </div>
                    <TextArea
                        value={headerValue}
                        name="customHeader"
                        onChange={(event) => onHeaderChange(event.target.value)}
                        placeholder="Header message goes here."
                        maxLength={144}
                        style={{ resize: 'none' }}
                    />
                </Form.Item>
                <Divider />
                <Form.Item>
                    <div className="form-label flexbox align-center">
                        <div className="step-counter flexbox align-center justify-center">3</div>
                        Report footer
                    </div>
                    <TextArea
                        value={footerValue}
                        name="customFooter"
                        onChange={(event) => onFooterChange(event.target.value)}
                        placeholder="Footer message goes here."
                        maxLength={144}
                        style={{ resize: 'none' }}
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isLoading}
                    >
                        Save template
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}
